import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, Navigation, CheckCircle2, ChevronRight, ArrowRight } from "lucide-react";
import { SERVICES } from "../config/content";
import { BUSINESS, mapsDirUrl } from "../config/business";
import SEO from "../components/SEO";
import { FadeUp, MaskedLine } from "../components/anim";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `${service.title} in Modesto, CA`,
      provider: { "@type": "AutoRepair", name: BUSINESS.name, telephone: BUSINESS.phoneDisplay },
      areaServed: BUSINESS.cities.map((c) => c.name),
      description: service.intro,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `https://${BUSINESS.domain}/` },
        { "@type": "ListItem", position: 2, name: "Services", item: `https://${BUSINESS.domain}/services` },
        { "@type": "ListItem", position: 3, name: service.title, item: `https://${BUSINESS.domain}/services/${slug}` },
      ],
    },
  ];

  const img = slug.includes("diesel") || slug.includes("repair") ? BUSINESS.images.equipment : slug.includes("star") ? BUSINESS.images.smogTest : BUSINESS.images.storefront;

  return (
    <div data-testid="service-detail">
      <SEO
        title={`${service.keyword} | ${BUSINESS.name}`}
        description={service.intro}
        keywords={`${service.keyword}, smog check Modesto, ${service.title} Modesto`}
        path={`/services/${slug}`}
        schema={schema}
      />
      <div className="section pt-8">
        <nav className="flex items-center gap-1.5 text-xs text-ink/50 font-body">
          <Link to="/" className="hover:text-volt">Home</Link><ChevronRight className="h-3 w-3" />
          <Link to="/services" className="hover:text-volt">Services</Link><ChevronRight className="h-3 w-3" />
          <span className="text-ink">{service.title}</span>
        </nav>
      </div>

      <section className="section py-8 grid lg:grid-cols-[1.5fr_1fr] gap-10 items-start">
        <div>
          <h1 className="font-head font-extrabold text-4xl sm:text-5xl text-ink">
            <MaskedLine lines={[{ text: service.title, className: "text-ink" }, { text: "in Modesto, CA", className: "text-volt" }]} />
          </h1>
          <p className="mt-5 text-lg text-ink/70 font-body">{service.intro}</p>
          <FadeUp className="mt-8">
            <img src={img} alt={`${service.title} in Modesto`} className="w-full h-[320px] object-cover rounded-2xl border border-line shadow-sm" loading="lazy" />
          </FadeUp>
          <div className="mt-8 card-soft p-6">
            <h2 className="font-head font-bold text-xl text-ink">What's Included</h2>
            <ul className="mt-4 grid sm:grid-cols-2 gap-3">
              {service.included.map((it) => (
                <li key={it} className="flex items-start gap-2 text-sm text-ink/80 font-body"><CheckCircle2 className="h-5 w-5 text-openg shrink-0" />{it}</li>
              ))}
            </ul>
          </div>
          <p className="mt-6 text-ink/70 font-body leading-relaxed">{service.body}</p>
        </div>

        <aside className="lg:sticky lg:top-24 space-y-4">
          <div className="card-soft p-6">
            <h3 className="font-head font-bold text-ink">Get {service.title} Today</h3>
            <p className="mt-1 text-sm text-ink/60 font-body">{BUSINESS.address.full}</p>
            <div className="mt-4 flex flex-col gap-2">
              <a href={`tel:${BUSINESS.phoneTel}`} className="btn-volt w-full"><Phone className="h-4 w-4" /> Call {BUSINESS.phoneDisplay}</a>
              <a href={mapsDirUrl()} target="_blank" rel="noreferrer" className="btn-outline w-full"><Navigation className="h-4 w-4" /> Get Directions</a>
              <Link to="/contact" className="btn-cta w-full">Request a Callback</Link>
            </div>
          </div>
          <div className="card-soft p-6">
            <h3 className="font-head font-bold text-ink">Related Services</h3>
            <ul className="mt-3 space-y-2">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link to={`/services/${r.slug}`} className="flex items-center justify-between gap-2 text-sm font-body text-ink/80 hover:text-volt">{r.title}<ArrowRight className="h-4 w-4" /></Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
